import axios from "axios";
import { useState, useEffect } from "react";
import { Alert, Box, Container, TextField, Tooltip } from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import LoadingPanel from "../../../components/common/LoadingPanel";
import { Search } from "@mui/icons-material";
import AddressManagement from "../../../components/common/AddressManagement";

export default function AddOrEditClient() {
  const { clientModel, setClientModel, getClientTypes, config, CLIENTS_API } =
    useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState({ show: false });

  const getclientTypesData = async () => {
    const clientT = await getClientTypes();
    setClientTypes(clientT);
  };

  const getClientInfoByIdentification = async () => {
    try {
      if (clientModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: clientModel.Identification },
              Value: clientModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: clientModel.IdentificationType.Id },
              Value: clientModel.IdentificationType.Id,
            },
          ]),
          pagination: -1,
        },
      };
      const data = await axios.get(CLIENTS_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setClientModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_client_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getclientTypesData();
  }, []);

  const handleChange = (e) => {
    setClientModel({
      ...clientModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setClientModel({
        ...clientModel,
        IdentificationType: identificationType,
      });
    }
  };

  const [clientTypes, setClientTypes] = useState(null);

  const handleClientTypeChange = (selectedClientType) => {
    if (!selectedClientType) {
      setClientModel({
        ...clientModel,
        clientTypeId: 0,
        ClientType: null,
      });
      return;
    }

    setClientModel({
      ...clientModel,
      clientTypeId: selectedClientType.Id,
      ClientType: selectedClientType,
    });
  };

  const isSearchAvailable = () =>
    clientModel?.Identification?.replaceAll(" ", "").length > 0;

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getClientInfoByIdentification();
    }
  };

  return (
    <div>
      <Container
        sx={{ paddingLeft: "0px !important;", paddingRight: "0px !important;" }}
        style={{ width: "100%" }}
      >
        <div className="w-full mt-2">
          <section className="flex justify-center content-center items-center ">
            {isLoading ? (
              <LoadingPanel />
            ) : (
              <section id="ReportData" className="w-full">
                <div className="flex justify-between my-3">
                  <Combobox
                    isRequired
                    id="IdentificationType"
                    handleValueChange={handleIdentificationTypeChange}
                    fieldValue={clientModel?.IdentificationType ?? null}
                    fieldOptions={[
                      {
                        Id: "Cédula de ciudadanía",
                        Name: "Cédula de ciudadanía",
                      },
                      { Id: "Nit", Name: "Nit" },
                      { Id: "Pasaporte", Name: "Pasaporte" },
                      {
                        Id: "Cédula de extranjería",
                        Name: "Cédula de extranjería",
                      },
                      { Id: "Otro", Name: "Otro" },
                    ]}
                    fieldLabel={localize.identification_type}
                  />

                  <div className="mx-2" />

                  <Box className="flex gap-2 items-center w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      name="Identification"
                      value={clientModel.Identification}
                      id="Identification"
                      label={localize.identification}
                      variant="outlined"
                      onChange={handleChange}
                      onKeyUp={onKeyUp}
                      autoComplete="off"
                    />

                    <Tooltip title={localize.client_search_tooltip}>
                      <button
                        disabled={!isSearchAvailable()}
                        style={{
                          color: isSearchAvailable() ? "#10366e" : "lightgray",
                        }}
                        onClick={getClientInfoByIdentification}
                      >
                        <Search
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </button>
                    </Tooltip>
                  </Box>
                </div>

                {showWarning?.show && (
                  <Alert className="mb-3" severity={showWarning.severity}>
                    <label className="text-xs">{showWarning.message}</label>
                  </Alert>
                )}

                {/* client types */}
                <div className="flex mb-3">
                  <Combobox
                    isRequired
                    id="ClientType"
                    handleValueChange={handleClientTypeChange}
                    fieldValue={clientModel?.ClientType}
                    fieldOptions={clientTypes ?? []}
                    fieldLabel={localize.client_type}
                  />
                </div>

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Name"
                  id="Name"
                  value={clientModel.Name}
                  label={localize.name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <AddressManagement
                  model={clientModel}
                  setModel={setClientModel}
                />

                <div className="flex justify-between my-3">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="PhoneNumber"
                    id="PhoneNumber"
                    value={clientModel.PhoneNumber}
                    label={localize.phone_number}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  <div className="mx-2" />
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="Email"
                    id="Email"
                    value={clientModel.Email}
                    label={localize.email}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  value={clientModel.ContactName}
                  name="ContactName"
                  id="ContactName"
                  label={localize.contact_name}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </section>
            )}
          </section>
        </div>
      </Container>
    </div>
  );
}
